/**
 * CardGridRenderer — card-grid
 * 1–3 column grid of cards; each card has title + optional badge + optional body.
 */
import React from "react";
import { SurfaceCard } from "../../ui/SurfaceCard";
import { Title } from "../../ui/Title";
import { Text } from "../../ui/Text";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText, resolveTextOptional } from "../../utils/resolveText";
import { SortableList } from "../SortableList";

function Badge({ label }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "var(--radius-full)",
        background: "var(--color-bg-accent)",
        color: "var(--color-text-primary)",
        fontSize: "var(--text-xs)",
        fontFamily: "var(--font-body)",
        fontWeight: 600,
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

export function CardGridRenderer({
  element,
  editing,
  onChange,
  onReorder,
  playing,
  isPaused,
  currentTimeSec,
}) {
  const items = element.items || [];
  const title = element.title ? resolveText(element.title) : null;
  const columns = element.columns || Math.min(items.length, 3) || 1;

  return (
    <SurfaceCard compact>
      <div style={{ display: "grid", gap: "var(--space-6)" }}>
        {title && (
          <AnimatedField
            boxAnimation={element.title?.boxAnimation ?? null}
            boxExitAnimation={element.title?.boxExitAnimation ?? null}
            playing={playing}
            isPaused={isPaused}
            currentTimeSec={currentTimeSec}
          >
            <Title size="xl">
              <EditableText
                value={title}
                editing={editing}
                onChange={(v) => onChange?.("title", v)}
              />
            </Title>
          </AnimatedField>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${Math.round(100 / columns)}% - var(--space-4, 16px)), 1fr))`,
            gap: "var(--space-4)",
            alignItems: "stretch",
          }}
        >
          <SortableList
            editing={editing}
            items={items}
            direction="horizontal"
            onReorder={(newItems) => onReorder?.("items", newItems)}
            renderItem={(item, i) => {
              const cardTitle = resolveText(item.title);
              const badge = resolveTextOptional(item.badge);
              const body = resolveTextOptional(item.body);
              const cardBox = item.boxAnimation ?? item.title?.boxAnimation ?? null;
              const cardExit = item.boxExitAnimation ?? item.title?.boxExitAnimation ?? null;
              return (
                <AnimatedField
                  key={i}
                  boxAnimation={cardBox}
                  boxExitAnimation={cardExit}
                  playing={playing}
                  isPaused={isPaused}
                  currentTimeSec={currentTimeSec}
                >
                  <div style={{ position: "relative", height: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "var(--space-3)",
                        padding: "var(--surface-padding-compact)",
                        background: "var(--color-bg-surface-strong)",
                        border: "var(--stroke-thin) solid var(--color-border-subtle)",
                        borderRadius: "var(--surface-radius)",
                        height: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                          gap: "var(--space-2)",
                        }}
                      >
                        <Title size="lg">
                          <EditableText
                            value={cardTitle}
                            editing={editing}
                            onChange={(v) => onChange?.(`items.${i}.title`, v)}
                          />
                        </Title>
                        {badge && <Badge label={badge} />}
                      </div>
                      {(body !== null || editing) && (
                        <Text role="caption">
                          <EditableText
                            value={body ?? ""}
                            editing={editing}
                            onChange={(v) => onChange?.(`items.${i}.body`, v)}
                            placeholder="• Add description…"
                          />
                        </Text>
                      )}
                    </div>
                    {editing && (
                      <button
                        type="button"
                        aria-label="Delete card"
                        onClick={() =>
                          onReorder?.(
                            "items",
                            items.filter((_, idx) => idx !== i)
                          )
                        }
                        style={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          width: 20,
                          height: 20,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "none",
                          borderRadius: "var(--radius-sm, 4px)",
                          background: "transparent",
                          color: "var(--color-text-muted, #94a3b8)",
                          cursor: "pointer",
                          fontSize: 16,
                          lineHeight: 1,
                          padding: 0,
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </AnimatedField>
              );
            }}
          />
        </div>
        {editing && (
          <button
            type="button"
            aria-label="Add card"
            onClick={() =>
              onReorder?.("items", [...items, { title: "New card", body: "Description" }])
            }
            style={{
              width: "100%",
              padding: "6px",
              border: "1px dashed var(--color-border-subtle, rgba(255,255,255,0.12))",
              borderRadius: "var(--radius-sm, 4px)",
              background: "transparent",
              color: "var(--color-text-muted, #94a3b8)",
              cursor: "pointer",
              fontSize: "var(--text-sm, 13px)",
              fontFamily: "var(--font-body, sans-serif)",
              textAlign: "center",
            }}
          >
            + Add card
          </button>
        )}
      </div>
    </SurfaceCard>
  );
}

export default CardGridRenderer;
